import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { cn } from "@/lib/cn.js";

export interface PropsControl {
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

/**
 * Un campo de formulario accesible: etiqueta asociada, ayuda y error unidos al
 * control con aria-describedby, y aria-invalid cuando hay error. El hijo es el
 * control (un input, select o textarea) o una función que recibe esas props
 * para ponerlas en el control que corresponda.
 */
export function Campo({
  etiqueta,
  ayuda,
  error,
  obligatorio,
  accion,
  className,
  children,
}: Readonly<{
  etiqueta: ReactNode;
  /** Un botón chico al lado de la etiqueta (fuera de ella: un label no lleva controles adentro). */
  accion?: ReactNode;
  ayuda?: ReactNode;
  error?: string;
  obligatorio?: boolean;
  className?: string;
  children: ReactElement<PropsControl> | ((ctl: PropsControl) => ReactNode);
}>) {
  const id = useId();
  const idAyuda = `${id}-ayuda`;
  const idError = `${id}-error`;
  const describe = [ayuda ? idAyuda : null, error ? idError : null].filter(Boolean).join(" ") || undefined;
  const ctl: PropsControl = { id, "aria-invalid": error ? true : undefined, "aria-describedby": describe };
  let control: ReactNode = null;
  if (typeof children === "function") control = children(ctl);
  else if (isValidElement(children)) control = cloneElement(children, ctl);
  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium block">
          {etiqueta}
          {obligatorio && (
            <span className="text-rose-600" aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </label>
        {accion}
      </div>
      {control}
      {ayuda && (
        <p id={idAyuda} className="text-xs text-slate-500">
          {ayuda}
        </p>
      )}
      {error && (
        <p id={idError} className="text-xs text-rose-600">
          {error}
        </p>
      )}
    </div>
  );
}

/** Grupo de casillas o radios con su leyenda y su error. */
export function GrupoCampos({
  leyenda,
  ayuda,
  error,
  className,
  children,
}: Readonly<{ leyenda: ReactNode; ayuda?: ReactNode; error?: string; className?: string; children: ReactNode }>) {
  const id = useId();
  return (
    <fieldset className={cn("space-y-2", className)} aria-describedby={error ? `${id}-error` : undefined}>
      <legend className="text-sm font-medium">{leyenda}</legend>
      {ayuda && <p className="text-xs text-slate-500">{ayuda}</p>}
      {children}
      {error && (
        <p id={`${id}-error`} className="text-xs text-rose-600">
          {error}
        </p>
      )}
    </fieldset>
  );
}
